import { Base } from "ui/Scripts/source/framework/base";
import { handleResponse, handleError, authorizationHeaderRefreshTokenValue, getJsonHeaders, getFormDataHeaders, handleFileResponse, getJsonHeadersPublic } from "./baseService";
import { SessionState } from "../session/sessionState";
import { Session } from "../session/session";
import { Translations } from "ui/Scripts/source/models/translations";
import { NotificationManager } from "../notification/notificationManager";
import { LoginResult, ILoginResult } from "../login/loginResult";
import * as store from "../../framework/customStore";
import * as storeActions from "../store/storeActions";
import { IEndlessList, EndlessList } from "ui/Scripts/source/models/common/endlessList";

export const login = async (username: string, password: string): Promise<ILoginResult> => {
    const requestOptions = {
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json;charset=utf-8",
            "Authorization": "Basic " + Base.b64EncodeUnicode(username) + ":" + Base.b64EncodeUnicode(password),
        }
    };
    const data = await fetch(appConfig.rootUrl + "api/token", requestOptions)
        .then(handleResponse, handleError);
    if (!data || !data.accessToken) {
        return null;
    }
    const sessionState = new SessionState(data);
    appConfig.culture = sessionState.culture;
    Translations.setCulture(appConfig.culture);
    await Session.setSessionState(sessionState);
    NotificationManager.requestNotificationPermission();
    return new LoginResult(sessionState);
};

export const logout = async (): Promise<void> => {
    try {
        await NotificationManager.removePushSubscriptions();
    } finally {
        await Session.setSessionState(null);
    }
    return new Promise<void>((resolve) => { resolve(); });
};

const getRefreshTokenRequestOptions = () => {
    return {
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json;charset=utf-8",
            "Authorization": authorizationHeaderRefreshTokenValue(),
        }
    };
};

const getRefreshTokenResult = async (data) => {
    if (!data || !data.accessToken) {
        return null;
    }
    const sessionState = new SessionState(data);
    appConfig.culture = sessionState.culture;
    Translations.setCulture(appConfig.culture);
    await Session.setSessionState(sessionState);
    return new LoginResult(sessionState);
};

export const refreshLogin = (): Promise<ILoginResult> => {
    return fetch(appConfig.rootUrl + "api/token/refresh", getRefreshTokenRequestOptions())
        .then(handleResponse, handleError)
        .then(
            data => {
                return getRefreshTokenResult(data)
                    .then(loginResult => {
                        return loginResult;
                    });
            });
};

export const refreshAuthentication = (force: boolean = false): Promise<void> => {
    if (!force && !Session.tokenNeedsRefresh()) {
        return new Promise((resolve, reject) => {
            resolve();
        });
    }
    return refreshLogin()
        .then((loginResult) => {
            store.customStore.dispatch(storeActions.loginSuccess(loginResult));
        });
};

export const changeCompany = async (companyId: string): Promise<LoginResult> => {
    const data = await fetch(appConfig.rootUrl + "api/token/changecompany?id=" + companyId, getRefreshTokenRequestOptions())
        .then(handleResponse, handleError);
    const loginResult = await getRefreshTokenResult(data);
    store.customStore.dispatch(storeActions.loginSuccess(loginResult));
    return loginResult;
};

export const getApiCall = <T>(url: string, type: (new(...args: any[]) => T)): Promise<T> => {
    return refreshAuthentication().then(() => {
        const requestOptions = {
            headers: getJsonHeaders()
        };
        return fetch(appConfig.rootUrl + url, requestOptions).then(handleResponse, handleError).then(data => {
            /* eslint-disable new-cap */
            return type
                ? !data ? new type() : new type(data)
                : !data ? <T><unknown>0 : <T>(data.result);
            /* eslint-enable new-cap */
        });
    });
};

export const getApiCallPublic = <T>(url: string, type: (new(...args: any[]) => T)): Promise<T> => {
    const requestOptions = {
        headers: getJsonHeadersPublic()
    };
    return fetch(appConfig.rootUrl + url, requestOptions).then(handleResponse, handleError).then(data => {
        /* eslint-disable new-cap */
        return type
            ? !data ? new type() : new type(data)
            : !data ? <T><unknown>0 : <T>(data.result);
        /* eslint-enable new-cap */
    });
};

export const getFileApiCall = (url: string): Promise<string> => {
    return refreshAuthentication().then(() => {
        const requestOptions = {
            //cache: "no-cache" as RequestCache,
            headers: getJsonHeaders()
        };
        // + "&ts=" + Base.getTimeStamp()
        return fetch(appConfig.rootUrl + url, requestOptions).then(handleFileResponse, handleError);
    });
};

export const postApiCall = <T>(url: string, type: (new(...args: any[]) => T), body: string = null): Promise<T> => {
    return refreshAuthentication().then(() => {
        const requestOptions = {
            method: "POST",
            headers: getJsonHeaders(),
            body
        };
        return fetch(appConfig.rootUrl + url, requestOptions).then(handleResponse, handleError).then(data => {
            /* eslint-disable new-cap */
            return !data ? new type() : new type(data);
            /* eslint-enable new-cap */
        });
    });
};

export const postFormApiCall = <T>(url: string, data: FormData, type: (new(...args: any[]) => T)): Promise<T> => {
    return refreshAuthentication().then(() => {
        const requestOptions = {
            method: "POST",
            headers: getFormDataHeaders(),
            body: data
        };
        return fetch(appConfig.rootUrl + url, requestOptions).then(handleResponse, handleError).then(data => {
            /* eslint-disable new-cap */
            return !data ? new type() : new type(data);
            /* eslint-enable new-cap */
        });
    });
};

export const postEndlessListApiCall = <T>(url: string, type: (new (...args: any[]) => T), body: string = null): Promise<IEndlessList<T>> => {
    return refreshAuthentication().then(() => {
        const requestOptions = {
            method: "POST",
            headers: getJsonHeaders(),
            body
        };
        return fetch(appConfig.rootUrl + url, requestOptions)
            .then(handleResponse, handleError)
            .then(data => {
                return !data ? new EndlessList(null, type) : new EndlessList(data, type);
            });
    });
};